import React from 'react';
import Icon from '../AppIcon';

const ProgressIndicator = ({ 
  currentStep = 1, 
  totalSteps = 4, 
  percentage = 0, 
  type = 'steps', // 'steps' | 'circular' | 'linear'
  size = 'default', // 'sm' | 'default' | 'lg'
  showLabels = true,
  steps = [],
  className = ''
}) => {
  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'current';
    return 'upcoming';
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return {
          container: 'text-xs',
          step: 'w-6 h-6',
          icon: 12,
          text: 'text-xs'
        };
      case 'lg':
        return {
          container: 'text-base',
          step: 'w-10 h-10',
          icon: 18,
          text: 'text-base'
        };
      default:
        return {
          container: 'text-sm',
          step: 'w-8 h-8',
          icon: 16,
          text: 'text-sm'
        };
    }
  };

  const sizeClasses = getSizeClasses();

  if (type === 'circular') {
    const circumference = 2 * Math.PI * 45;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className={`flex flex-col items-center space-y-2 ${className}`}>
        <div className="relative">
          <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
            {/* Background circle */}
            <circle
              cx="50"
              cy="50"
              r="45"
              stroke="currentColor"
              strokeWidth="8"
              fill="transparent"
              className="text-muted"
            />
            {/* Progress circle */}
            <circle
              cx="50"
              cy="50"
              r="45"
              stroke="currentColor"
              strokeWidth="8"
              fill="transparent"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              className="text-primary transition-all duration-300 ease-in-out"
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-semibold font-mono text-foreground">
              {Math.round(percentage)}%
            </span>
          </div>
        </div>
        {showLabels && (
          <div className="text-center">
            <div className="text-sm font-medium text-foreground">Progress</div>
            <div className="text-xs text-muted-foreground">
              {currentStep} of {totalSteps} completed
            </div>
          </div>
        )}
      </div>
    );
  }

  if (type === 'linear') {
    return (
      <div className={`w-full space-y-2 ${className}`}>
        {showLabels && (
          <div className="flex justify-between items-center">
            <span className={`font-medium text-foreground ${sizeClasses?.text}`}>
              Progress
            </span>
            <span className={`font-mono text-muted-foreground ${sizeClasses?.text}`}>
              {Math.round(percentage)}%
            </span>
          </div>
        )}
        <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300 ease-in-out rounded-full"
            style={{ width: `${percentage}%` }}
          />
        </div>
        {showLabels && (
          <div className={`text-center text-muted-foreground ${sizeClasses?.text}`}>
            {currentStep} of {totalSteps} steps completed
          </div>
        )}
      </div>
    );
  }

  // Default: steps type
  return (
    <div className={`w-full ${sizeClasses?.container} ${className}`}>
      <div className="flex items-center justify-between">
        {Array.from({ length: totalSteps }, (_, index) => {
          const stepNumber = index + 1;
          const status = getStepStatus(stepNumber);
          const stepData = steps?.[index] || { label: `Step ${stepNumber}` };

          return (
            <React.Fragment key={stepNumber}>
              <div className="flex flex-col items-center space-y-2">
                {/* Step Circle */}
                <div
                  className={`
                    ${sizeClasses?.step} rounded-full flex items-center justify-center font-medium
                    transition-all duration-200 ease-in-out
                    ${status === 'completed'
                      ? 'bg-success text-success-foreground shadow-sm'
                      : status === 'current' ?'bg-primary text-primary-foreground shadow-md ring-2 ring-primary/20' :'bg-muted text-muted-foreground'
                    }
                  `}
                >
                  {status === 'completed' ? (
                    <Icon name="Check" size={sizeClasses?.icon} />
                  ) : (
                    <span className="font-mono">{stepNumber}</span>
                  )}
                </div>

                {/* Step Label */}
                {showLabels && (
                  <div className="text-center max-w-20">
                    <div
                      className={`
                        font-medium leading-tight
                        ${status === 'current' ?'text-foreground'
                          : status === 'completed' ?'text-success' :'text-muted-foreground'
                        }
                        ${sizeClasses?.text}
                      `}
                    >
                      {stepData?.label}
                    </div>
                    {stepData?.description && (
                      <div className={`text-muted-foreground mt-1 ${sizeClasses?.text === 'text-xs' ? 'text-[10px]' : 'text-xs'}`}>
                        {stepData?.description}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Connector Line */}
              {index < totalSteps - 1 && (
                <div className="flex-1 mx-2 mt-0">
                  <div
                    className={`
                      h-0.5 transition-all duration-300 ease-in-out
                      ${status === 'completed'
                        ? 'bg-success' :'bg-muted'
                      }
                    `}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {/* Overall Progress Bar */}
      {type === 'steps' && (
        <div className="mt-4 w-full bg-muted rounded-full h-1 overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300 ease-in-out"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default ProgressIndicator;